import { useEffect, useRef } from 'react';
import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { z } from 'zod';

import { Spinner } from '@/components/ui/spinner';
import { useAuth } from '@/lib/auth/store';

/**
 * /viewer/sso?grant=<id> — the landing route for SSO-mode client-viewer
 * invitations (Tier 2). Unlike magic-link viewers, SSO viewers sign in
 * with their own Entra account; this route just pins the viewer grant id
 * (sent as X-Viewer-Grant-Id) before handing off to the normal auth flow.
 */
export const Route = createFileRoute('/viewer/sso')({
  validateSearch: z.object({ grant: z.string().optional() }),
  component: ViewerSsoScreen,
});

function ViewerSsoScreen() {
  const navigate = useNavigate();
  const { grant } = Route.useSearch();
  const setViewerGrant = useAuth((s) => s.setViewerGrant);
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    if (grant) {
      setViewerGrant(grant);
      void navigate({ to: '/' });
    }
  }, [grant, setViewerGrant, navigate]);

  return (
    <div className="grid min-h-screen place-items-center bg-paper-50 p-6">
      <div className="w-full max-w-md space-y-4 rounded-modal bg-paper-0 p-8 shadow-modal">
        <h1 className="text-h2 font-semibold text-ink-900">Client viewer access</h1>
        {grant ? (
          <div className="flex items-center gap-2 text-body-sm text-ink-700">
            <Spinner /> Preparing your viewer session…
          </div>
        ) : (
          <p className="text-body-sm text-ink-700">
            This link is missing its grant reference. Open the invitation link exactly as it appears
            in your email, or contact your Avero lead. You can also{' '}
            <Link to="/login" className="font-medium text-accent-amber-700 underline">
              sign in
            </Link>{' '}
            directly.
          </p>
        )}
      </div>
    </div>
  );
}
